import { useState } from 'react'
import { useApp } from '../../context/AppContext'

/**
 * Componente de formulario para la creación de nuevos estados de tickets.
 * * Permite definir el nombre, color representativo, orden de visualización y
 * si el estado se considera como una etapa de cierre (finalización) del ticket.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Function} [props.onAdded] - Callback que se ejecuta tras una inserción exitosa en Supabase.
 * Útil para refrescar listas o cerrar diálogos modales.
 * * @returns {JSX.Element} Un formulario estructurado con validaciones de tipo de dato.
 */
const StatusAdd = ({ onAdded }) => {
  /** @type {Object} Cliente de Supabase obtenido del contexto de la aplicación */
  const { supabase } = useApp()

  /** @type {Object} Estado inicial para limpiar el formulario tras el guardado */
  const initialState = {
    name: '',
    color: '#000000',
    isClosed: false,
    orderIndex: 0,
  }

  /** @type {[Object, Function]} Estado local del formulario */
  const [newPayload, setNewPayload] = useState(initialState)

  /** @type {[boolean, Function]} Estado para controlar el bloqueo del botón de envío */
  const [loading, setLoading] = useState(false)

  /**
   * Manejador genérico para cambios en los inputs.
   * * Realiza conversiones automáticas basadas en el tipo de input:
   * - Checkbox: Retorna valor booleano (`checked`).
   * - Number: Convierte el string a número.
   * - Otros: Retorna el string plano.
   * * @param {React.ChangeEvent<HTMLInputElement>} e - Evento de cambio nativo.
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target

    setNewPayload((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : name === 'orderIndex'
            ? Number(value)
            : value,
    }))
  }

  /**
   * Procesa el envío de datos a la tabla `status` en el esquema `Tickets`.
   * * Incluye validación de campos requeridos y manejo de errores asíncronos.
   * @async
   * @param {React.FormEvent} e - Evento de envío del formulario.
   */
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validación básica de nombre no vacío
    if (!newPayload.name.trim()) {
      alert('El nombre es requerido')
      return
    }

    setLoading(true)

    try {
      const { error } = await supabase
        .schema('Tickets')
        .from('status')
        .insert([newPayload])

      if (error) throw error

      // Resetear el formulario al estado inicial
      setNewPayload(initialState)

      // Ejecutar callback de éxito si existe
      if (onAdded) onAdded()
    } catch (error) {
      console.error('Error adding status:', error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <form className="sf" onSubmit={handleSubmit}>
      <div className="row g-2">
        {/* NAME */}
        <div className="col-12">
          <label className="sf-label" htmlFor="statusAddName">
            Nombre
          </label>
          <input
            id="statusAddName"
            type="text"
            className="form-control form-control-sm"
            name="name"
            value={newPayload.name}
            onChange={handleChange}
            placeholder="Ej. En revisión"
            required
          />
        </div>

        {/* COLOR */}
        <div className="col-6">
          <label className="sf-label" htmlFor="statusAddColor">
            Color
          </label>
          <div className="sf-color">
            <input
              id="statusAddColor"
              type="color"
              name="color"
              value={newPayload.color}
              onChange={handleChange}
            />
            <span>{newPayload.color.toUpperCase()}</span>
          </div>
        </div>

        {/* ORDER */}
        <div className="col-6">
          <label className="sf-label" htmlFor="statusAddOrder">
            Orden
          </label>
          <input
            id="statusAddOrder"
            type="number"
            className="form-control form-control-sm"
            name="orderIndex"
            value={newPayload.orderIndex}
            onChange={handleChange}
            min={0}
          />
        </div>

        {/* CHECK */}
        <div className="col-12">
          <div className="form-check form-switch m-0 sf-check">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              name="isClosed"
              checked={newPayload.isClosed}
              onChange={handleChange}
              id="isClosedCheck"
            />
            <label className="form-check-label" htmlFor="isClosedCheck">
              Es estado de cierre
            </label>
          </div>
        </div>
      </div>

      {/* BUTTON */}
      <div className="sf-actions">
        <button
          type="submit"
          className="btn btn-sm btn-primary"
          disabled={loading}
        >
          {loading ? (
            <span className="spinner-border spinner-border-sm me-2" />
          ) : (
            <i className="fa-solid fa-plus me-2" />
          )}
          {loading ? 'Guardando...' : 'Agregar estado'}
        </button>
      </div>

      <style>{`
        .sf-label {
          display: block; margin-bottom: 4px; font-size: 0.72rem; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.04em; color: var(--gm-text-muted);
        }
        .sf-color {
          display: flex; align-items: center; gap: 8px; height: 31px; padding: 0 8px;
          border: 1px solid var(--gm-border); border-radius: 6px; background: var(--gm-surface);
          font-size: 0.8rem; font-variant-numeric: tabular-nums; color: var(--gm-text);
        }
        .sf-color input[type='color'] {
          width: 20px; height: 20px; padding: 0; border: none; border-radius: 4px;
          background: transparent; cursor: pointer;
        }
        .sf-color input[type='color']::-webkit-color-swatch-wrapper { padding: 0; }
        .sf-color input[type='color']::-webkit-color-swatch { border: none; border-radius: 4px; }
        .sf-check { font-size: 0.85rem; color: var(--gm-text); }
        .sf-actions { display: flex; justify-content: flex-end; gap: 6px; margin-top: 14px; }
      `}</style>
    </form>
  )
}

export default StatusAdd
